import { create } from "zustand";
import {
  StaffAttendance,
  StaffShift,
  AttendanceType,
} from "@/models/attendanceModel";
import * as attendanceService from "@/services/attendance/attendanceService";

interface AttendanceState {
  currentAttendance: StaffAttendance | null;
  attendanceHistory: StaffAttendance[];
  monthlyAttendance: {
    totalHours: number;
    records: StaffAttendance[];
    byType: Record<AttendanceType, number>;
  } | null;
  shifts: StaffShift[];
  currentShift: StaffShift | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchCurrentAttendance: (staffId: string) => Promise<void>;
  fetchAttendanceHistory: (
    staffId: string,
    storeId?: string,
    startDate?: Date,
    endDate?: Date
  ) => Promise<void>;
  fetchMonthlyAttendance: (
    staffId: string,
    year: number,
    month: number
  ) => Promise<void>;
  fetchShifts: (staffId: string, storeId?: string) => Promise<void>;
  fetchCurrentShift: (staffId: string) => Promise<void>;
  clockIn: (
    staffId: string,
    storeId: string,
    type?: AttendanceType,
    description?: string
  ) => Promise<void>;
  clockOut: (attendanceId: string, description?: string) => Promise<void>;
  createAbsenceRecord: (
    staffId: string,
    storeId: string,
    type: AttendanceType,
    startDate: Date,
    endDate: Date,
    description?: string
  ) => Promise<void>;
  createOrUpdateShift: (
    shift: Omit<StaffShift, "id" | "created_at" | "updated_at">
  ) => Promise<void>;
  deleteShift: (id: string) => Promise<void>;
}

export const useAttendanceStore = create<AttendanceState>((set, get) => ({
  currentAttendance: null,
  attendanceHistory: [],
  monthlyAttendance: null,
  shifts: [],
  currentShift: null,
  isLoading: false,
  error: null,

  fetchCurrentAttendance: async (staffId: string) => {
    set({ isLoading: true, error: null });
    try {
      const attendance = await attendanceService.getCurrentAttendanceRecord(
        staffId
      );
      set({ currentAttendance: attendance, isLoading: false });
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    }
  },

  fetchAttendanceHistory: async (
    staffId: string,
    storeId?: string,
    startDate?: Date,
    endDate?: Date
  ) => {
    set({ isLoading: true, error: null });
    try {
      const history = await attendanceService.fetchAttendanceRecords(
        staffId,
        storeId,
        startDate,
        endDate
      );
      set({ attendanceHistory: history, isLoading: false });
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    }
  },

  fetchMonthlyAttendance: async (
    staffId: string,
    year: number,
    month: number
  ) => {
    set({ isLoading: true, error: null });
    try {
      const summary = await attendanceService.getMonthlyAttendanceSummary(
        staffId,
        year,
        month
      );
      set({ monthlyAttendance: summary, isLoading: false });
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    }
  },

  fetchShifts: async (staffId: string, storeId?: string) => {
    set({ isLoading: true, error: null });
    try {
      const shifts = await attendanceService.fetchStaffShifts(staffId, storeId);
      set({ shifts, isLoading: false });
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    }
  },

  fetchCurrentShift: async (staffId: string) => {
    set({ isLoading: true, error: null });
    try {
      const shift = await attendanceService.getCurrentShift(staffId);
      set({ currentShift: shift, isLoading: false });
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    }
  },

  clockIn: async (
    staffId: string,
    storeId: string,
    type: AttendanceType = "work",
    description?: string
  ) => {
    set({ isLoading: true, error: null });
    try {
      const attendance = await attendanceService.clockIn(
        staffId,
        storeId,
        type,
        description
      );
      set({
        currentAttendance: attendance,
        isLoading: false,
        attendanceHistory: [attendance, ...get().attendanceHistory],
      });
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    }
  },

  clockOut: async (attendanceId: string, description?: string) => {
    set({ isLoading: true, error: null });
    try {
      const attendance = await attendanceService.clockOut(
        attendanceId,
        description
      );
      set({
        currentAttendance: null,
        isLoading: false,
        attendanceHistory: get().attendanceHistory.map((record) =>
          record.id === attendance.id ? attendance : record
        ),
      });
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    }
  },

  createAbsenceRecord: async (
    staffId: string,
    storeId: string,
    type: AttendanceType,
    startDate: Date,
    endDate: Date,
    description?: string
  ) => {
    set({ isLoading: true, error: null });
    try {
      const attendance = await attendanceService.createAbsenceRecord(
        staffId,
        storeId,
        type,
        startDate,
        endDate,
        description
      );
      set({
        isLoading: false,
        attendanceHistory: [attendance, ...get().attendanceHistory],
      });
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    }
  },

  createOrUpdateShift: async (
    shift: Omit<StaffShift, "id" | "created_at" | "updated_at">
  ) => {
    set({ isLoading: true, error: null });
    try {
      const updatedShift = await attendanceService.createOrUpdateShift(shift);

      // Update shifts list by replacing if exists or adding if new
      const updatedShifts = get().shifts.some(
        (s) =>
          s.staff_id === updatedShift.staff_id &&
          s.day_of_week === updatedShift.day_of_week
      )
        ? get().shifts.map((s) =>
            s.staff_id === updatedShift.staff_id &&
            s.day_of_week === updatedShift.day_of_week
              ? updatedShift
              : s
          )
        : [...get().shifts, updatedShift];

      set({ shifts: updatedShifts, isLoading: false });
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    }
  },

  deleteShift: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await attendanceService.deleteShift(id);
      set({
        shifts: get().shifts.filter((shift) => shift.id !== id),
        isLoading: false,
      });
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    }
  },
}));
